import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { useAuth } from '@r-client/shared/data/auth';
import { Text } from '@r-client/shared/ui/core';
import { FieldModel, validation } from '@r-client/shared/util/model';

import { useCreateRsvpMutation } from '../../graphql/create-rsvp-mutation';
import { useGetUserReservationForEventQuery } from '../../graphql/get-user-reservation-for-event-query';
import { TGetEventsEventDetails } from '../../types';
import { Form } from './form/form';
import { Spinner } from './spinner/spinner';
import { SuccessView } from './success-view/success-view';

export interface IRsvpContainerProps {
  event: Omit<NonNullable<TGetEventsEventDetails>, 'offerings'>;
}

export const RsvpContainer = observer<IRsvpContainerProps>(
  function RsvpContainer({ event }) {
    const { viewer } = useAuth();
    const [justSubscribed, setJustSubscribed] = useState(false);
    const userReservation = useGetUserReservationForEventQuery({
      variables: { slug: event.slug },
    });

    const rsvpModel = useCreateRsvpMutation({
      fields: {
        eventId: new FieldModel<string>(),
        fullName: new FieldModel<string>(validation.required()),
        email: new FieldModel<string>(
          validation.compose(
            validation.required(),
            validation.emailValidation()
          )
        ),
        linkedinUrl: new FieldModel<string>(
          validation.urlValidation(),
          undefined
        ),
        phoneNumber: new FieldModel<string>(
          validation.phoneValidation(),
          undefined
        ),
      },
    });

    useEffect(() => {
      if (viewer?.isAuthenticated) {
        const user = viewer?.info;
        rsvpModel.fields.email.value = user?.email;
        rsvpModel.fields.fullName.value = `${user?.firstName} ${user?.lastName}`;
      }
    }, [
      rsvpModel.fields.email,
      rsvpModel.fields.fullName,
      viewer?.isAuthenticated,
      viewer?.info,
    ]);

    const handleFormSubmit = async () => {
      rsvpModel.fields.eventId.value = event.id;
      const result = await rsvpModel.submit();

      if (result) {
        setJustSubscribed(true);
        userReservation.refetch();
      }
    };

    if (userReservation.isRejected) {
      return <Text align="center">Something went wrong, try again later.</Text>;
    }

    if (userReservation.isLoading) {
      return <Spinner />;
    }

    const checkableEvent = userReservation.data?.event;
    if (!checkableEvent) return null;

    if (justSubscribed || checkableEvent.userSubscribed) {
      const message = justSubscribed
        ? 'Done!'
        : 'You already subscribed to this event.';

      return <SuccessView message={message} event={event} />;
    }

    return <Form event={event} model={rsvpModel} onSubmit={handleFormSubmit} />;
  }
);
